import {
  Creative,
  Placement,
  PlacementStatus,
  PlacementStatusType
} from './non-live-report-constants';
import { equalsIgnoreCase } from '../../utils/text-utils';

// A non-live creative counts as "insignificant" when it barely ran, so it's flagged
// yellow rather than red.
export const isYellow = (creative: Creative, totalCount: number) =>
  !creative.live && (creative.count / totalCount < 0.05 || creative.count < 100);

// A creative is trafficked through an ad server that doesn't match its source — it's live,
// but running somewhere unexpected. This is the second reason (besides being non-live) a
// placement gets flagged red.
export const isSourceMismatch = (creative: Creative, adServer: string | null) =>
  !adServer || !creative.campaign_source || !equalsIgnoreCase(adServer, creative.campaign_source);

// Rolls a placement's creatives up into a single status: red if any creative isn't live
// or its source doesn't match the ad server, yellow if the only issues are insignificant,
// green when everything is live and matched. Mirrors the original bucketing logic.
export const getPlacementStatus = (placement: Placement): PlacementStatusType => {
  let status: PlacementStatusType = PlacementStatus.GREEN;
  for (const creative of placement.iscis) {
    if (isYellow(creative, placement.count)) {
      status = PlacementStatus.YELLOW;
    } else if (!creative.live || isSourceMismatch(creative, placement.adServer)) {
      return PlacementStatus.RED;
    }
  }
  return status;
};

export type PlacementWithStatus = Placement & { status: PlacementStatusType };

const STATUS_SORT_ORDER: PlacementStatusType[] = [
  PlacementStatus.RED,
  PlacementStatus.YELLOW,
  PlacementStatus.GREEN
];

// Computes each placement's status once, then orders worst-first (red, then yellow, then
// green), alphabetized by network within each status group. Returns the status attached so
// callers don't recompute it.
export const sortPlacementsByStatus = (placements: Placement[]): PlacementWithStatus[] => {
  const withStatus = placements.map<PlacementWithStatus>(placement => ({
    ...placement,
    status: getPlacementStatus(placement)
  }));
  return STATUS_SORT_ORDER.flatMap(status =>
    withStatus
      .filter(placement => placement.status === status)
      .sort((a, b) => a.network.toUpperCase().localeCompare(b.network.toUpperCase()))
  );
};

// Per-row copy and per-cell flags. Rather than flag the whole row, only the cell that
// actually shows the problem is highlighted: the Live? cell when a creative isn't live,
// or the Source + Ad Server cells when they don't match.
const RED = 'cell-flag-red';
const YELLOW = 'cell-flag-yellow';

export const getRowInfo = (creative: Creative, groupCount: number, adServer: string | null) => {
  if (isYellow(creative, groupCount))
    return { liveText: 'no, but insignificant', liveClass: YELLOW, sourceClass: '' };
  if (!creative.live) return { liveText: 'no!', liveClass: RED, sourceClass: '' };
  if (isSourceMismatch(creative, adServer))
    return { liveText: 'yes', liveClass: '', sourceClass: RED };
  return { liveText: 'yes', liveClass: '', sourceClass: '' };
};
